import React, { Component, ReactNode } from 'react';
import './AttachmentErrorBoundary.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class AttachmentErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error for debugging
    console.error('Attachment Error Boundary caught an error:', error, errorInfo);

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="attachment-error-boundary">
          <div className="attachment-error-content">
            <div className="attachment-error-icon">⚠️</div>
            <h3 className="attachment-error-title">Upload Error</h3>
            <p className="attachment-error-message">
              Something went wrong with the image upload. This might be due to:
            </p>
            <ul className="attachment-error-list">
              <li>File size too large</li>
              <li>Unsupported file format</li>
              <li>Network connection issues</li>
              <li>Server temporarily unavailable</li>
            </ul>
            <div className="attachment-error-actions">
              <button 
                onClick={this.handleRetry}
                className="attachment-error-retry-button"
              >
                Try Again
              </button>
              <button 
                onClick={() => window.location.reload()}
                className="attachment-error-reload-button"
              >
                Reload Page
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && (
              <details className="attachment-error-details">
                <summary>Error Details (Development)</summary>
                <pre className="attachment-error-stack">
                  {this.state.error?.stack}
                </pre>
                <pre className="attachment-error-info">
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Specialized error boundary for upload operations
export class UploadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    console.error('Upload Error Boundary caught an error:', error, errorInfo);

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="upload-error-boundary">
          <div className="upload-error-content">
            <div className="upload-error-icon">📤</div>
            <h3 className="upload-error-title">Upload Failed</h3>
            <p className="upload-error-message">
              The image upload failed. Please check your file and try again.
            </p>
            <div className="upload-error-actions">
              <button 
                onClick={this.handleRetry}
                className="upload-error-retry-button"
              >
                Retry Upload
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
